import { ChangeEvent, ChangeEventHandler, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CasesIcon,
  Field,
  FieldTypes,
  OrganizationIcon,
  SelectDrawer,
  SpeedDialAction,
  SpeedDialIcon,
  useBreakpoints,
} from 'ui';
import { PageHeaderToolbar } from '@components/PageHeaderToolbar';
import { useLayout } from '@context/LayoutContext';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import {
  SpeedDialWrapper,
  StyledCaseFieldWrapper,
  StyledFieldsContainer,
  StyledHeader,
  StyledOrgFieldWrapper,
  StyledPendingSetupChip,
} from './SecondaryTopNavbar.styles';

export function SecondaryTopNavbar() {
  const {
    userOrganizations,
    userCases,
    selectedOrganization,
    selectedCase,
    handleOrganizationSelect,
    handleCaseSelect,
  } = useUserOrgCase();

  const navigate = useNavigate();
  const { isMdUp } = useBreakpoints();
  const {
    isSpeedDialOpen,
    handleSpeedDialClose,
    handleSpeedDialOpen,
    toolbarItems,
  } = useLayout();

  const [isOrganizationsDrawerOpen, setIsOrganizationsDrawerOpen] =
    useState(false);
  const [isCasesDrawerOpen, setIsCasesDrawerOpen] = useState(false);

  const isPermissionsUrl = location.pathname.includes('permissions');
  const isHistoryUrl = location.pathname.includes('history');

  const organizationItems = useMemo(
    () =>
      userOrganizations.map(({ organizationID, organizationName }) => ({
        key: organizationID,
        value: organizationName,
      })),
    [userOrganizations],
  );

  const caseItems = useMemo(
    () =>
      userCases.map(({ caseID, caseName, isClaimscoreEnabled }) => ({
        key: caseID,
        value: caseName,
        rightChip: !isClaimscoreEnabled && (
          <StyledPendingSetupChip label="Pending Setup" size="small" />
        ),
      })),
    [userCases],
  );

  const mobileToolbarActions = useMemo(
    () =>
      toolbarItems.map(({ name, icon, onClick }) => (
        <SpeedDialAction
          icon={icon}
          key={name}
          onClick={onClick}
          tooltipTitle={name}
        />
      )),
    [toolbarItems],
  );

  const handleOrganizationChange: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const inputOrganizationId = e.target.value;
    const inputOrganization = userOrganizations.find(
      ({ organizationID }) => organizationID === inputOrganizationId,
    );
    if (!inputOrganization) return;

    if (isPermissionsUrl) {
      navigate(`/permissions/organization${location.search}`);
    }
    handleOrganizationSelect(inputOrganization);
  };

  const handleCaseChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputCaseId = e.target.value;
    const inputCase = userCases.find(({ caseID }) => caseID === inputCaseId);
    if (!inputCase) return;

    if (isPermissionsUrl) {
      navigate(`/permissions/case${location.search}`);
    }
    handleCaseSelect(inputCase);
  };
  if (isMdUp) {
    return (
      <StyledHeader data-testid="secondary-header-container">
        <StyledFieldsContainer>
          <StyledOrgFieldWrapper>
            <Field
              disabled={userOrganizations.length === 0}
              label="Organization"
              menuItems={organizationItems || []}
              onChange={handleOrganizationChange}
              showError={false}
              type={'select' as FieldTypes}
              value={selectedOrganization?.organizationID || ''}
              width="100%"
            />
          </StyledOrgFieldWrapper>
          <StyledCaseFieldWrapper>
            <Field
              disabled={userCases.length === 0 || isHistoryUrl}
              label="Case"
              menuItems={caseItems || []}
              onChange={handleCaseChange}
              showError={false}
              type={'select' as FieldTypes}
              value={selectedCase?.caseID || ''}
              width="100%"
            />
          </StyledCaseFieldWrapper>
        </StyledFieldsContainer>
        <PageHeaderToolbar />
      </StyledHeader>
    );
  }

  return (
    <>
      <SpeedDialWrapper
        ariaLabel="Utilities"
        icon={<SpeedDialIcon />}
        onClose={handleSpeedDialClose}
        onOpen={handleSpeedDialOpen}
        open={isSpeedDialOpen}
      >
        <SpeedDialAction
          disabled={userOrganizations.length === 0}
          icon={<OrganizationIcon />}
          onClick={() => {
            setIsOrganizationsDrawerOpen(true);
          }}
          tooltipTitle="Organization"
        />
        {!isHistoryUrl && (
          <SpeedDialAction
            disabled={userCases.length === 0}
            icon={<CasesIcon />}
            onClick={() => {
              setIsCasesDrawerOpen(true);
            }}
            tooltipTitle="Case"
          />
        )}
        {mobileToolbarActions}
      </SpeedDialWrapper>
      <SelectDrawer
        handleClose={() => setIsOrganizationsDrawerOpen(false)}
        handleOpen={() => setIsOrganizationsDrawerOpen(true)}
        isOpen={isOrganizationsDrawerOpen}
        label="Organization"
        onChange={(key) => {
          handleOrganizationChange({
            target: { value: key },
          } as ChangeEvent<HTMLInputElement>);
          setIsOrganizationsDrawerOpen(false);
        }}
        options={organizationItems || []}
        value={selectedOrganization?.organizationID || ''}
      />
      <SelectDrawer
        handleClose={() => setIsCasesDrawerOpen(false)}
        handleOpen={() => setIsCasesDrawerOpen(true)}
        isOpen={isCasesDrawerOpen}
        label="Cases"
        onChange={(key) => {
          handleCaseChange({
            target: { value: key },
          } as ChangeEvent<HTMLInputElement>);
          setIsCasesDrawerOpen(false);
        }}
        options={caseItems || []}
        value={selectedCase?.caseID || ''}
      />
    </>
  );
}
